import type { ReactElement } from 'react';
import type { FabProps } from '@mui/material/Fab';

import { cloneElement } from 'react';
import { useBackToTop } from 'minimal-shared/hooks';

import Fab from '@mui/material/Fab';

import { Iconify } from '../iconify';

// ----------------------------------------------------------------------

/**
 * 맨 위로 올려 주는 떠 있는 버튼. 스크롤이 임계점을 넘으면 나타난다.
 *
 * ⚠️ **필수 프롭이 없다** — 실제 호출은 전부 `<BackToTopButton />` 이다.
 *
 * ⚠️ `renderButton` 을 주면 **기본 `Fab` 을 안 그린다.** 대신 그 결과에 `onClick` 을
 * 얹어 복제하므로, 돌려주는 것은 `onClick` 을 받을 수 있는 단일 엘리먼트여야 한다.
 */
export type BackToTopButtonProps = Omit<FabProps, 'children'> & {
  /** 스크롤 판정을 디바운스할지 */
  isDebounce?: boolean;
  /** 버튼을 직접 그린다. 주면 기본 `Fab` 을 대체한다 */
  renderButton?: (isVisible: boolean) => ReactElement;
  /** 나타날 지점. 기본 `'90%'` */
  scrollThreshold?: string | number;
};

export function BackToTopButton({
  sx,
  isDebounce,
  renderButton,
  scrollThreshold = '90%',
  ...other
}: BackToTopButtonProps) {
  const { onBackToTop, isVisible } = useBackToTop(scrollThreshold, isDebounce);

  if (renderButton) {
    return cloneElement(renderButton(isVisible), {
      onClick: onBackToTop,
    } as Partial<unknown>);
  }

  return (
    <Fab
      aria-label="Back to top"
      onClick={onBackToTop}
      sx={[
        (theme) => ({
          width: 48,
          height: 48,
          position: 'fixed',
          transform: 'scale(0)',
          right: { xs: 24, md: 32 },
          bottom: { xs: 24, md: 32 },
          zIndex: theme.zIndex.speedDial,
          transition: theme.transitions.create(['transform']),
          ...(isVisible && { transform: 'scale(1)' }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Iconify width={24} icon="solar:double-alt-arrow-up-linear" />
    </Fab>
  );
}
